/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';

import { getProposalItems } from 'services/actions/ai_management_proposal';
import { getProposalCheck } from 'services/actions/ai_management_proposal_check';
import {
  ProposalDocumentsResponse,
  getProposalDocuments,
} from 'services/actions/ai_management_proposal_documents';
import type { Proposal } from 'services/ai_management_proposal';
import {
  FinalFile,
  buildAfterBidItems,
  buildPortalReminders,
  buildFinalFiles,
  buildFinalZip,
  fetchFinalFile,
  saveBlob,
  zipFilename,
} from 'services/ai_management_proposal_final';
import { formatCurrency } from 'services/conlicitaseg';
import { Container } from './styles';

interface AiManagementProposalFinalProps {
  biddingId: number;
  proposal: Proposal;
  editalNumber?: string | null;
  onEdit: () => void;
}

interface Resumo {
  checagem: string;
  declaracoes: string;
  itens: string;
  total: string;
}

const ok = <T,>(res: { data: T; status?: number } | undefined): T | null =>
  res && (res.status ?? 200) < 400 ? res.data : null;

// Tela final ("Gerada"): resumo, os arquivos que vão antes do lance (download, prévia e zip) e as
// exigências que ficam para depois do lance.
const AiManagementProposalFinal: React.FC<AiManagementProposalFinalProps> = ({
  biddingId,
  proposal,
  editalNumber,
  onEdit,
}) => {
  const [documentos, setDocumentos] = useState<ProposalDocumentsResponse | null>(null);
  const [resumo, setResumo] = useState<Resumo | null>(null);
  const [selecionados, setSelecionados] = useState<string[]>([]);
  const [baixando, setBaixando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [previa, setPrevia] = useState<{ id: string; url: string | null; erro: boolean } | null>(
    null,
  );
  const previaUrl = useRef<string | null>(null);

  useEffect(() => {
    let vivo = true;
    Promise.all([
      getProposalDocuments(biddingId).catch(() => undefined),
      getProposalCheck(biddingId).catch(() => undefined),
      getProposalItems(biddingId, { per_page: 1 }).catch(() => undefined),
    ]).then(([docsRes, checkRes, itemsRes]) => {
      if (!vivo) return;
      const docs = ok(docsRes);
      const check = ok(checkRes);
      const items = ok(itemsRes);
      setDocumentos(docs && Array.isArray(docs.declaracoes) ? docs : null);
      setResumo({
        checagem: check?.contadores
          ? `${check.contadores.atendidas} de ${check.contadores.total} regras atendidas`
          : '—',
        declaracoes: docs?.progresso
          ? `${docs.progresso.confirmadas} de ${docs.progresso.total} confirmados`
          : '—',
        itens: items?.resumo
          ? `${items.resumo.itens_selecionados} ${
              items.resumo.itens_selecionados === 1 ? 'item' : 'itens'
            } na proposta`
          : '—',
        total: items?.resumo ? formatCurrency(items.resumo.total_proposta) || '—' : '—',
      });
    });
    return () => {
      vivo = false;
    };
  }, [biddingId, proposal.generatedAt]);

  const arquivos = useMemo(
    () => buildFinalFiles(biddingId, proposal.generatedFiles, documentos),
    [biddingId, proposal.generatedFiles, documentos],
  );
  const depoisDoLance = useMemo(() => buildAfterBidItems(documentos), [documentos]);
  const lembretes = useMemo(() => buildPortalReminders(documentos), [documentos]);
  const todos = arquivos.length > 0 && selecionados.length === arquivos.length;

  const liberarPrevia = () => {
    if (previaUrl.current) window.URL.revokeObjectURL(previaUrl.current);
    previaUrl.current = null;
  };
  useEffect(() => liberarPrevia, []);

  const alternar = (id: string) =>
    setSelecionados((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  const baixarUm = (arquivo: FinalFile) => {
    setErro(null);
    fetchFinalFile(arquivo.fonte)
      .then((blob) => saveBlob(blob, arquivo.filename))
      .catch(() => setErro('Não foi possível baixar o arquivo. Tente de novo.'));
  };

  const alternarPrevia = (arquivo: FinalFile) => {
    liberarPrevia();
    if (previa?.id === arquivo.id) {
      setPrevia(null);
      return;
    }
    setPrevia({ id: arquivo.id, url: null, erro: false });
    fetchFinalFile(arquivo.fonte)
      .then((blob) => {
        // O tipo do blob nem sempre vem do back; o visualizador de PDF do navegador precisa dele.
        const tipo = arquivo.previa === 'pdf' ? 'application/pdf' : blob.type;
        const url = window.URL.createObjectURL(new Blob([blob], { type: tipo }));
        previaUrl.current = url;
        setPrevia((atual) => (atual?.id === arquivo.id ? { ...atual, url } : atual));
      })
      .catch(() =>
        setPrevia((atual) => (atual?.id === arquivo.id ? { ...atual, erro: true } : atual)),
      );
  };

  const baixarSelecionados = async () => {
    setBaixando(true);
    setErro(null);
    try {
      const { blob, falhas } = await buildFinalZip(
        arquivos.filter((arquivo) => selecionados.includes(arquivo.id)),
      );
      if (blob) saveBlob(blob, zipFilename(editalNumber));
      if (falhas > 0) {
        setErro(
          `Não foi possível baixar ${falhas} ${
            falhas === 1 ? 'arquivo' : 'arquivos'
          }. Tente de novo.`,
        );
      }
    } finally {
      setBaixando(false);
    }
  };

  const renderPrevia = (arquivo: FinalFile) => {
    if (previa?.id !== arquivo.id) return null;
    let conteudo: React.ReactNode = (
      <span className="text-muted">Carregando pré-visualização…</span>
    );
    if (previa.erro) {
      conteudo = <span className="text-muted">Não foi possível carregar a pré-visualização.</span>;
    } else if (previa.url && arquivo.previa === 'pdf') {
      conteudo = <iframe title={`Pré-visualização de ${arquivo.filename}`} src={previa.url} />;
    } else if (previa.url) {
      conteudo = <img src={previa.url} alt={`Pré-visualização de ${arquivo.filename}`} />;
    }
    return <div className="files-preview">{conteudo}</div>;
  };

  const geradaEm = proposal.generatedAt ? moment.parseZone(proposal.generatedAt) : null;

  return (
    <Container>
      <div className="generated-head">
        <span className="generated-badge">
          <i className="material-icons-outlined" aria-hidden="true">
            check_circle
          </i>
          Gerada
        </span>
        <span>
          Gerada
          {geradaEm && ` em ${geradaEm.format('DD/MM/YYYY')} às ${geradaEm.format('HH:mm')}`}
          {proposal.generatedByName && ` por ${proposal.generatedByName}`}
        </span>
      </div>

      <div className="generated-stats">
        {[
          ['Checagem', resumo?.checagem],
          ['Declarações e documentos', resumo?.declaracoes],
          ['Itens', resumo?.itens],
          ['Total da proposta', resumo?.total],
        ].map(([rotulo, valor]) => (
          <div key={rotulo} className="stat-card">
            <small>{rotulo}</small>
            <strong>{valor ?? '…'}</strong>
          </div>
        ))}
      </div>

      <section className="generated-files">
        <div className="files-toolbar">
          <div>
            <h2>Arquivos gerados</h2>
            <small>
              Gerado em: Preparar Proposta · Os mesmos arquivos ficam em Arquivos gerados pelo
              licitante, na categoria Proposta.
            </small>
          </div>
          <button type="button" className="btn btn-outline-secondary" onClick={onEdit}>
            <i className="material-icons-outlined" aria-hidden="true">
              edit
            </i>
            Editar proposta
          </button>
        </div>

        <div className="files-bulk">
          <label className="mb-0" htmlFor="final-selecionar-todos">
            <input
              id="final-selecionar-todos"
              type="checkbox"
              checked={todos}
              disabled={arquivos.length === 0}
              onChange={() => setSelecionados(todos ? [] : arquivos.map((arquivo) => arquivo.id))}
            />
            Selecionar todos os arquivos
          </label>
          <div className="d-flex align-items-center">
            {selecionados.length > 0 && (
              <>
                <span className="text-muted mr-2" aria-live="polite">
                  {selecionados.length === 1
                    ? '1 arquivo selecionado'
                    : `${selecionados.length} arquivos selecionados`}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-link mr-2"
                  onClick={() => setSelecionados([])}
                >
                  Limpar seleção
                </button>
              </>
            )}
            <button
              type="button"
              className="btn btn-primary"
              disabled={selecionados.length === 0 || baixando}
              onClick={baixarSelecionados}
            >
              {baixando ? 'Preparando download…' : 'Download'}
            </button>
          </div>
        </div>

        {erro && <div className="alert alert-danger py-2">{erro}</div>}

        <div className="files-group">
          <h3>Antes do lance</h3>
          <div className="files-table">
            <div className="files-row files-head">
              <span className="files-check" />
              <span className="files-name">Nome do arquivo</span>
              <span className="files-kind">Tipo</span>
              <span className="files-actions" />
            </div>
            {arquivos.map((arquivo) => (
              <React.Fragment key={arquivo.id}>
                <div className={`files-row${selecionados.includes(arquivo.id) ? ' selected' : ''}`}>
                  <span className="files-check">
                    <input
                      type="checkbox"
                      aria-label={`Selecionar ${arquivo.filename}`}
                      checked={selecionados.includes(arquivo.id)}
                      onChange={() => alternar(arquivo.id)}
                    />
                  </span>
                  <span className="files-name">{arquivo.filename}</span>
                  <span className="files-kind">{arquivo.tipo}</span>
                  <span className="files-actions">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary icon-only"
                      aria-label={`Baixar ${arquivo.filename}`}
                      title="Baixar"
                      onClick={() => baixarUm(arquivo)}
                    >
                      <i className="material-icons-outlined" aria-hidden="true">
                        file_download
                      </i>
                    </button>
                    {arquivo.previa && (
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary icon-only"
                        aria-expanded={previa?.id === arquivo.id}
                        aria-label={
                          previa?.id === arquivo.id
                            ? 'Fechar pré-visualização'
                            : 'Pré-visualizar arquivo'
                        }
                        onClick={() => alternarPrevia(arquivo)}
                      >
                        <i className="material-icons-outlined" aria-hidden="true">
                          {previa?.id === arquivo.id ? 'expand_less' : 'expand_more'}
                        </i>
                      </button>
                    )}
                  </span>
                </div>
                {renderPrevia(arquivo)}
              </React.Fragment>
            ))}
            {lembretes.map((item) => (
              <div key={item.id} className="files-row">
                <span className="files-check">
                  <input
                    type="checkbox"
                    disabled
                    aria-label={`${item.nome}: assinalar no portal, sem arquivo`}
                  />
                </span>
                <span className="files-name">{item.nome}</span>
                <span className="files-kind">{item.tipo}</span>
                <span className="files-actions">
                  <span className={`files-selo ${item.selo.className}`}>{item.selo.rotulo}</span>
                </span>
              </div>
            ))}
            {arquivos.length === 0 && lembretes.length === 0 && (
              <div className="files-row">
                <span className="text-muted">Nenhum arquivo gerado.</span>
              </div>
            )}
          </div>
        </div>

        {depoisDoLance.length > 0 && (
          <div className="files-group">
            <h3>Após o lance</h3>
            <div className="files-table">
              <div className="files-row files-head">
                <span className="files-check" />
                <span className="files-name">Nome do arquivo</span>
                <span className="files-kind">Tipo</span>
                <span className="files-actions" />
              </div>
              {depoisDoLance.map((item) => (
                <div key={item.id} className="files-row">
                  <span className="files-check">
                    <input
                      type="checkbox"
                      disabled
                      aria-label={`${item.nome}: fica para depois do lance`}
                    />
                  </span>
                  <span className="files-name">{item.nome}</span>
                  <span className="files-kind">{item.tipo}</span>
                  <span className="files-actions">
                    <span className={`files-selo ${item.selo.className}`}>{item.selo.rotulo}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </Container>
  );
};

export default AiManagementProposalFinal;
